import { useState, useEffect } from 'react';
import { css, jsx } from '@emotion/react';
import { Img } from '../Img/Img.es.js';
import { Animation } from '../../../theme/Animation.es.js';
import '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { boxSizing } from '../../../style-mixins/reset.es.js';

const getNameInitials = (name) => {
  const initials = (name || "").trim().split(/\s+/).map((s) => s[0]);
  const firstLetter = initials[0];
  const lastLetter = initials[initials.length - 1];
  if (!firstLetter) {
    return "";
  }
  return initials.length === 1 ? String(firstLetter) : `${firstLetter}${lastLetter}`;
};
const SIZES = {
  small: 24,
  medium: 36,
  large: 48
};
const Styles = {
  root: (hasError, props) => [
    css`
      ${boxSizing};
      position: relative;
      overflow: hidden;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      border: var(--zds-border-width-default) solid
        var(--zds-brand-almost-white);
      border-radius: var(--zds-radius-pill);
      line-height: 1;
      user-select: none;
      transition: ${Animation.transitionValue};

      &[data-block] {
        display: flex;
      }

      &[data-interactive] {
        &:hover,
        &:focus {
          border-color: var(--zds-stroke-weaker);
        }
      }

      &[data-selected][data-selected] {
        border: none;
        outline: var(--zds-border-focus);
        outline-offset: 2px;
      }
    `,
    !props.url || props.url && hasError ? css`
          &:nth-of-type(3n + 1),
          &[data-color='primary'][data-color] {
            background-color: var(--zds-color-blue-2);
            color: var(--zds-text-stronger);
          }

          &:nth-of-type(3n + 2),
          &[data-color='secondary'][data-color] {
            background-color: var(--zds-color-teal-2);
            color: var(--zds-text-stronger);
          }

          &:nth-of-type(3n),
          &[data-color='tertiary'][data-color] {
            background-color: var(--zds-color-pink-2);
            color: var(--zds-text-stronger);
          }
        ` : null,
    css`
      &[data-size='small'] {
        height: var(--zds-size-small);
        width: var(--zds-size-small);
        font: var(--zds-minimal-print-3);
      }

      &[data-size='medium'] {
        height: var(--zds-size-medium);
        width: var(--zds-size-medium);
        font: var(--zds-body-3);
        border: var(--zds-border-weak);
      }

      &[data-size='large'] {
        height: var(--zds-size-large);
        width: var(--zds-size-large);
        font: var(--zds-paragraph-header-3);
        border: var(--zds-border-weak);
      }
    `
  ],
  img: css`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `
};
const Avatar = ({ "aria-hidden": ariaHidden, color, isBlock, isInteractive, isSelected, name, url, shouldLazilyLoad = true, size = "small" }) => {
  const [hasError, setHasError] = useState(false);
  const initials = name ? getNameInitials(name) : null;
  useEffect(() => {
    if (hasError) {
      setHasError(false);
    }
  }, [url]);
  return jsx("div", { "aria-hidden": ariaHidden, "aria-label": name, css: Styles.root(hasError, { url }), "data-block": isBlock ? "true" : void 0, "data-color": color, "data-interactive": isInteractive ? "true" : void 0, "data-selected": isSelected ? "true" : void 0, "data-size": size, "data-testid": "Avatar", "data-zds": true, role: "img" }, url && !hasError ? jsx(Img, { css: Styles.img, alt: name, "aria-hidden": true, height: SIZES[size], onError: () => setHasError(true), src: url, width: SIZES[size], loading: shouldLazilyLoad ? "lazy" : "eager" }) : jsx("span", null, initials));
};

export { Avatar };
//# sourceMappingURL=Avatar.es.js.map
